import React from 'react';
import { useQuery } from '@apollo/client';
import LoadingSpinner from '../../../../loading-spinner';
import { DocumentPermissions } from '../../../../../pages/document-wizard/shared/hooks/use-document-team-permissions/use-document-team-permissions';
import ClauseTemplatesSidebar from './clauseTemplatesSidebar';
import { getDocumentTemplates, getOrganizationsTemplatesByUser } from '../../../../../api/queries_v2';
import { TemplateStatus } from '../../../../../pages/templates/templateConstants';
import { TemplateType } from '../../../../../consts/forms-types';
import { DocumentTemplateModalType } from '../../../../../pages/deals/document-template-modal/DocumentTemplateModalContainer';
import { classifyClauseBody } from '../../docx-native/clauseBody';

// Single source of truth for the clause shape lives in the main container.
export type { ClauseTemplate } from './clauseTemplateSidebarContainer';
import type { ClauseTemplate } from './clauseTemplateSidebarContainer';

interface ClauseTemplateForTemplatesProps {
  permissions: DocumentPermissions;
  teamId: string | null | undefined;
}

const ClauseTemplateContainerForTemplates = ({ permissions, teamId }: ClauseTemplateForTemplatesProps) => {
  const { data: templatesData, loading: isTemplatesDataLoading } = useQuery(getDocumentTemplates, {
    variables: { teamId, templateType: 'clause', includeContent: true },
    skip: !teamId,
    fetchPolicy: 'network-only',
  });

  const { loading: organizationsTemplatesDataLoading, data: organizationsTemplatesData } = useQuery(
    getOrganizationsTemplatesByUser,
    {
      fetchPolicy: 'no-cache',
      variables: {
        teamId,
        templateType: 'clause',
        includeContent: true,
      },
      skip: !teamId,
    }
  );

  const filterByTemplateType = (template: DocumentTemplateModalType | ClauseTemplate) => {
    return template?.templateType === TemplateType.CLAUSE && template?.status === TemplateStatus.ACTIVE;
  };

  const filterOrgsTemplates = (templates: [DocumentTemplateModalType]) => {
    return templates?.filter((template: DocumentTemplateModalType) => filterByTemplateType(template));
  };

  const relevantOrgsTemplates = filterOrgsTemplates(organizationsTemplatesData?.organizationsTemplatesByUser) || [];

  const relevantTeamTemplates =
    templatesData?.getDocumentTemplates.filter((template: any) => filterByTemplateType(template)) || [];

  // For templates, we only disable if the user doesn't have edit permission
  // Templates are always editable when in template management mode
  const isUpdatingDisabled = !permissions.hasEditPermission;

  const allTemplates = [...relevantTeamTemplates, ...relevantOrgsTemplates];
  const uniqueTemplates = allTemplates.filter(
    (template, index, self) => index === self.findIndex((t) => t.id === template.id)
  );
  // Decode a legacy HTML clause body if base64-encoded. DOCX bodies stay as raw
  // base64 and are inserted as .docx fragments (see clauseBody.ts).
  const decodeHtmlBody = (str: string | undefined): string => {
    if (!str) return '';
    try {
      if (/^[A-Za-z0-9+/]*={0,2}$/.test(str) && str.length > 20) {
        return decodeURIComponent(escape(atob(str)));
      }
      return str;
    } catch {
      return str;
    }
  };

  const clauseTemplates: ClauseTemplate[] =
    uniqueTemplates.map((template: any) => {
      const raw = template.template.body as string | undefined;
      const format = classifyClauseBody(raw);
      return {
        body: format === 'docx' ? (raw ?? '') : decodeHtmlBody(raw),
        format,
        title: template.name,
        id: template.id,
      };
    }) || [];

  const sortedTemplates = clauseTemplates.sort((a, b) => a.title.localeCompare(b.title));

  if (isTemplatesDataLoading || organizationsTemplatesDataLoading) {
    return <LoadingSpinner />;
  }

  // Refetch function to refresh clause list after creation
  const handleClauseCreated = () => {
    // Trigger a refetch of both queries to update the clause list
    if (teamId) {
      // The Apollo Client will automatically refetch due to the refetchQueries in the mutation
      // No additional action needed here since CreateClauseSlideout handles the refetch
    }
  };

  return (
    <ClauseTemplatesSidebar
      clauseTemplates={sortedTemplates}
      isDisabled={isUpdatingDisabled}
      teamId={teamId}
      onClauseCreated={handleClauseCreated}
      canCreateClauses={!!(permissions?.hasEditTemplatesPermission || permissions?.hasManageTemplatesPermission)}
    />
  );
};

export default ClauseTemplateContainerForTemplates;
